import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { deliverPdf } from "@helpers";

// ── tipos ─────────────────────────────────────────────────
export type NoteType = "debit" | "credit";

export interface NotePdfItemI {
  product:   string;
  unit:      string | null;
  quantity:  number;
  unitPrice: number;
  subtotal:  number;
}

export interface NotePdfParams {
  noteType:             NoteType;
  id:                   string;
  type:                 string;
  typeCode:             string;
  amount:               number;
  notes?:               string | null;
  createdAt:            string | Date;
  branch:               string;
  customer?:            string | null;
  // referencias opcionales
  saleId?:              string | null;
  accountReceivableId?: string | null;
  // solo nota de crédito
  affectsInventory?:    boolean;
  items?:               NotePdfItemI[];
}

// ── constantes de layout ──────────────────────────────────
const PAGE_W    = 210;
const MARGIN    = 10;
const CONTENT_W = PAGE_W - MARGIN * 2;
const FOOTER_H  = 16;
const PAGE_H    = 297;
const HEADER_H  = 50;

// ── colores ───────────────────────────────────────────────
const PURPLE = [30, 25, 72]    as [number, number, number];
const RED    = [153, 60, 29]   as [number, number, number];
const GREEN  = [15, 110, 86]   as [number, number, number];
const WHITE  = [255, 255, 255] as [number, number, number];
const LIGHT  = [245, 244, 251] as [number, number, number];
const GRAY   = [136, 135, 128] as [number, number, number];
const DARK   = [44, 44, 42]    as [number, number, number];

const accentColor = (noteType: NoteType) =>
  noteType === "debit" ? RED : GREEN;

const noteLabel = (noteType: NoteType) =>
  noteType === "debit" ? "NOTA DE DÉBITO" : "NOTA DE CRÉDITO";

// ── header ────────────────────────────────────────────────
const drawHeader = (
  doc:      jsPDF,
  params:   NotePdfParams,
  dateStr:  string,
  pageNum:  number,
  totalPgs: number,
) => {
  const accent = accentColor(params.noteType);

  doc.setFillColor(...PURPLE);
  doc.rect(0, 0, PAGE_W, 28, "F");

  doc.setFillColor(...accent);
  doc.rect(0, 28, PAGE_W, 4, "F");

  doc.setTextColor(...WHITE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Stockify", MARGIN, 13);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("Sistema de inventario", MARGIN, 20);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(noteLabel(params.noteType), PAGE_W - MARGIN, 13, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(dateStr,                             PAGE_W - MARGIN, 20,   { align: "right" });
  doc.text(`Página ${pageNum} de ${totalPgs}`,  PAGE_W - MARGIN, 25.5, { align: "right" });

  doc.setFillColor(...LIGHT);
  doc.rect(0, 32, PAGE_W, 17, "F");

  const col2 = MARGIN + CONTENT_W * 0.40;
  const col3 = MARGIN + CONTENT_W * 0.70;

  doc.setTextColor(...GRAY);
  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  doc.text("CLIENTE",  MARGIN, 38);
  doc.text("SUCURSAL", col2,   38);
  doc.text("TIPO",     col3,   38);

  doc.setFont("helvetica", "normal");
  doc.setTextColor(...PURPLE);
  doc.setFontSize(9);
  doc.text(params.customer ?? "Sin cliente", MARGIN, 45, { maxWidth: col2 - MARGIN - 4 });
  doc.text(params.branch,                    col2,   45, { maxWidth: col3 - col2 - 4   });

  doc.setFillColor(...accent);
  doc.roundedRect(col3, 40, 50, 7, 2, 2, "F");
  doc.setTextColor(...WHITE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.text(params.type, col3 + 25, 45, { align: "center" });
};

// ── footer ────────────────────────────────────────────────
const drawFooter = (doc: jsPDF, dateStr: string, noteType: NoteType) => {
  const y = PAGE_H - FOOTER_H + 4;
  doc.setDrawColor(...LIGHT);
  doc.setLineWidth(0.4);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);

  doc.setFontSize(7);
  doc.setTextColor(...GRAY);
  doc.setFont("helvetica", "normal");
  doc.text(`${noteLabel(noteType)} — Sistema de inventario`, MARGIN, y + 5);
  doc.text(`Generado el ${dateStr}`, PAGE_W - MARGIN, y + 5, { align: "right" });
};

// ── función principal ─────────────────────────────────────
export const generateNotePdf = (params: NotePdfParams) => {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  const now     = new Date();
  const dateStr = now.toLocaleDateString("es-EC", {
    day: "2-digit", month: "long", year: "numeric",
  });

  const accent      = accentColor(params.noteType);
  const isCredit    = params.noteType === "credit";
  const hasItems    = isCredit && (params.items ?? []).length > 0;
  const sign        = isCredit ? "-" : "+";
  const amountColor = isCredit ? GREEN : RED;

  drawHeader(doc, params, dateStr, 1, 1);

  // ── tabla de info general ─────────────────────────────────
  const rows: [string, string][] = [
    ["Sucursal", params.branch],
    ["Cliente",  params.customer ?? "Sin cliente"],
    ["Tipo",     params.type],
    ["Fecha",    typeof params.createdAt === "string"
      ? params.createdAt
      : new Date(params.createdAt).toLocaleDateString("es-EC", {
          day: "2-digit", month: "long", year: "numeric",
        })],
  ];

  if (isCredit) {
    rows.push([
      "Afecta inventario",
      params.affectsInventory ? "Sí — descuenta stock" : "No",
    ]);
  }
  if (params.saleId) {
    rows.push(["Venta asociada", `ID: ${params.saleId}`]);
  }
  if (params.accountReceivableId) {
    rows.push(["CxC asociada", `ID: ${params.accountReceivableId}`]);
  }
  if (params.notes) {
    rows.push(["Notas", params.notes]);
  }

  autoTable(doc, {
    startY: HEADER_H + 4,
    head: [[
      { content: "Campo", styles: { halign: "left" } },
      { content: "Valor", styles: { halign: "left" } },
    ]],
    body: rows.map(([campo, valor]) => [
      { content: campo, styles: { fontStyle: "bold", halign: "left", textColor: GRAY } },
      { content: valor, styles: { halign: "left" } },
    ]),
    styles: {
      fontSize:    9,
      cellPadding: { top: 4, bottom: 4, left: 6, right: 6 },
      overflow:    "linebreak",
    },
    headStyles: { fillColor: PURPLE, textColor: WHITE, fontStyle: "bold", fontSize: 8.5 },
    alternateRowStyles: { fillColor: LIGHT },
    columnStyles: {
      0: { cellWidth: CONTENT_W * 0.32 },
      1: { cellWidth: CONTENT_W * 0.68 },
    },
    margin: { left: MARGIN, right: MARGIN, top: HEADER_H, bottom: FOOTER_H + 4 },
    didDrawPage: () => {
      const pg = (doc as any).internal.getNumberOfPages();
      drawHeader(doc, params, dateStr, pg, pg);
      drawFooter(doc, dateStr, params.noteType);
    },
  });

  let curY = (doc as any).lastAutoTable.finalY + 8;

  // ── tabla de items (solo nota de crédito con items) ───────
  if (hasItems) {
    const COL_PROD  = CONTENT_W * 0.40;
    const COL_UNIT  = CONTENT_W * 0.12;
    const COL_QTY   = CONTENT_W * 0.12;
    const COL_PRICE = CONTENT_W * 0.18;
    const COL_TOTAL = CONTENT_W * 0.18;

    autoTable(doc, {
      startY: curY,
      head: [[
        { content: "Producto",  styles: { halign: "left"   } },
        { content: "Unidad",    styles: { halign: "center" } },
        { content: "Cant.",     styles: { halign: "center" } },
        { content: "P. Unit.",  styles: { halign: "right"  } },
        { content: "Subtotal",  styles: { halign: "right"  } },
      ]],
      body: (params.items ?? []).map((i) => [
        { content: i.product,                              styles: { halign: "left",   overflow: "linebreak", cellWidth: COL_PROD  } },
        { content: i.unit ?? "—",                         styles: { halign: "center", cellWidth: COL_UNIT   } },
        { content: String(i.quantity),                    styles: { halign: "center", cellWidth: COL_QTY    } },
        { content: `$${Number(i.unitPrice).toFixed(2)}`,  styles: { halign: "right",  cellWidth: COL_PRICE  } },
        { content: `$${Number(i.subtotal).toFixed(2)}`,   styles: { halign: "right",  cellWidth: COL_TOTAL  } },
      ]),
      styles: {
        fontSize:    9,
        cellPadding: { top: 3.5, bottom: 3.5, left: 3, right: 3 },
        overflow:    "linebreak",
      },
      headStyles: { fillColor: GREEN, textColor: WHITE, fontStyle: "bold", fontSize: 8.5 },
      alternateRowStyles: { fillColor: LIGHT },
      columnStyles: {
        0: { cellWidth: COL_PROD  },
        1: { cellWidth: COL_UNIT  },
        2: { cellWidth: COL_QTY   },
        3: { cellWidth: COL_PRICE },
        4: { cellWidth: COL_TOTAL },
      },
      margin: { left: MARGIN, right: MARGIN, top: HEADER_H, bottom: FOOTER_H + 4 },
      didDrawPage: () => {
        const pg = (doc as any).internal.getNumberOfPages();
        drawHeader(doc, params, dateStr, pg, pg);
        drawFooter(doc, dateStr, params.noteType);
      },
    });

    curY = (doc as any).lastAutoTable.finalY + 8;
  }

  // ── bloque de monto / total ───────────────────────────────
  const totalsW = CONTENT_W * 0.40;
  const totalsX = PAGE_W - MARGIN - totalsW;
  const labelX  = totalsX + 4;
  const valueX  = totalsX + totalsW - 4;

  const totalsH = 8 + 7 + 11;
  if (curY + totalsH > PAGE_H - FOOTER_H) {
    doc.addPage();
    drawHeader(doc, params, dateStr, (doc as any).internal.getNumberOfPages(), 1);
    curY = HEADER_H + 4;
  }

  // encabezado totales
  doc.setFillColor(...LIGHT);
  doc.rect(totalsX, curY, totalsW, 8, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  doc.text("CONCEPTO", labelX, curY + 5.5);
  doc.text("MONTO",    valueX, curY + 5.5, { align: "right" });
  curY += 8;

  // fila concepto
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...DARK);
  doc.text(params.type, labelX, curY + 5);
  doc.setTextColor(...amountColor);
  doc.text(`${sign}$${Number(params.amount).toFixed(2)}`, valueX, curY + 5, { align: "right" });
  curY += 7;

  // fila TOTAL
  doc.setFillColor(...accent);
  doc.rect(totalsX, curY, totalsW, 11, "F");
  doc.setTextColor(...WHITE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("TOTAL", labelX, curY + 7.5);
  doc.text(`${sign}$${Number(params.amount).toFixed(2)}`, valueX, curY + 7.5, { align: "right" });

  // ── footer en todas las páginas ───────────────────────────
  const totalPages = (doc as any).internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    drawHeader(doc, params, dateStr, p, totalPages);
    drawFooter(doc, dateStr, params.noteType);
  }

  // ── entregar: preview en desktop, descarga directa en móvil/tablet ──
  const blob = doc.output("blob");
  deliverPdf(
    blob,
    `${isCredit ? "nota-credito" : "nota-debito"}-${params.id}.pdf`,
  );
};
